import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trash2 } from 'lucide-react';
import { addFileToProviderResourceByTeam, removeFileFromProviderResourceByTeam } from '../../../../../services/staffingService';

const DocumentsTab = ({ officerData, providerCompanyId, officers, setOfficers }) => {
    const [uploadedFile, setUploadedFile] = useState(null);
    const [documentType, setDocumentType] = useState(""); // State for document type
    const [isSaved, setIsSaved] = useState(false);

    // Document types for dropdown
    const documentTypes = ["Contract", "Service Agreement", "License" , "Certificate" , "Other"];

    // Helper to update officer data in parent component's state
    const updateOfficerDataState = (updatedOfficerData) => {
        const updatedOfficers = officers.map((officer) =>
            officer._id === updatedOfficerData._id ? { ...updatedOfficerData, files: updatedOfficerData.files || [] } : officer // Ensure files is always an array
        );
        setOfficers(updatedOfficers);
    };
    

    const handleRemoveExistingFile = async (fileId) => {
        try {
            await removeFileFromProviderResourceByTeam(providerCompanyId, officerData._id, fileId);
    
            // Ensure `files` is always an array
            const updatedOfficerData = {
                ...officerData,
                files: (officerData.files || []).filter((file) => file._id !== fileId), // Safely handle undefined `files`
            };
    
            // Update officerData state in the parent component
            updateOfficerDataState(updatedOfficerData);
            console.log(`File removed successfully: ${fileId}`);
        } catch (error) {
            console.error("Error removing file:", error);
        }
    };
    

    // Dropzone configuration for file upload
    const { getRootProps, getInputProps } = useDropzone({
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length > 0) {
                setUploadedFile(acceptedFiles[0]);
                setIsSaved(false);
            }
        },
        accept: 'application/pdf',
    });

    const handleSave = async () => {
        if (uploadedFile && documentType) {
            try {
                await addFileToProviderResourceByTeam(providerCompanyId, officerData._id, uploadedFile);
    
                const newFile = {
                    _id: Date.now(), // Temporary _id; replace with backend-generated _id after the actual API response
                    fileType: documentType,
                    fileName: uploadedFile.name,
                    fileUrl: URL.createObjectURL(uploadedFile), // Mock URL, replace with actual URL from API
                    uploadDate: new Date(),
                };
    
                // Ensure `files` array is always an array, even if it's initially undefined
                const updatedOfficerData = {
                    ...officerData,
                    files: [...(officerData.files || []), newFile], // Ensure files is an array
                };
    
                // Update officerData in parent state
                updateOfficerDataState(updatedOfficerData);
                //setIsSaved(true);
                setUploadedFile(null);
                setDocumentType("");
                console.log("File successfully uploaded");
            } catch (error) {
                console.error("Error saving file:", error);
            }
        } else {
            console.log("No file or document type selected");
        }
    };
    

    return (
        <div className="p-6">
            <input {...getInputProps()} />

            {/* Existing documents section */}
            <div className="mb-6">
                {officerData.files && officerData.files.length > 0 ? (
                    <div className="space-y-3">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Existing Documents</h3>
                        {officerData.files.map((file) => (
                            <div key={file._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                                <a
                                    href={file.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                                >
                                    {file.fileType} - {file.fileName}
                                </a>
                                <Trash2
                                    onClick={() => handleRemoveExistingFile(file._id)}
                                    className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer transition-colors duration-200"
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-500">No documents found.</p>
                    </div>
                )}
            </div>

            {/* Drag and drop section */}
            {!uploadedFile && !isSaved && (
                <div
                    {...getRootProps()}
                    className="border-2 border-dashed border-blue-300 rounded-lg p-10 text-center bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors duration-200"
                >
                    <input {...getInputProps()} />
                    <p className="text-blue-600 text-base">
                        Drag and drop a document here, or click to select one.
                    </p>
                </div>
            )}

            {/* Show uploaded file info, document type dropdown, and remove icon */}
            {uploadedFile && (
                <div className="mt-6 border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-gray-900 font-medium text-base">
                            {uploadedFile.name}
                        </p>
                        <Trash2
                            onClick={() => setUploadedFile(null)}
                            className="w-5 h-5 text-red-500 hover:text-red-700 cursor-pointer transition-colors duration-200"
                        />
                    </div>

                    {/* Dropdown for selecting document type */}
                    <div>
                        <label
                            htmlFor="documentType"
                            className="block text-sm font-medium text-gray-700 mb-2"
                        >
                            Select file type to save:
                        </label>
                        <select
                            id="documentType"
                            value={documentType}
                            onChange={(e) => setDocumentType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                            <option value="">--Select Type--</option>
                            {documentTypes.map((type) => (
                                <option key={type} value={type}>
                                    {type}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Save button */}
            {uploadedFile && documentType && (
                <button
                    className="mt-6 px-6 py-2 bg-[#404880] text-white rounded-md hover:bg-[#2d3461] transition-colors duration-200 font-medium"
                    onClick={handleSave}
                >
                    Save Document
                </button>
            )}
        </div>
    );
};

export default DocumentsTab;
